// Owner: members-extensible (owner feedback F9). Workspace members are extensible: the fields an
// admin adds to Members live on one built-in object (key `member`, trait `member_directory`) whose
// records mirror the members table one to one (same id). Everything that works for object fields
// (field types, field UI, validation, formulas, AI fields, filters, charts, workflows, field-level
// access) therefore works for member fields; this module holds the shared names and shapes.
import { z } from 'zod';

/** Api key of the built-in object that carries the member fields. */
export const MEMBER_OBJECT_KEY = 'member';

/** Trait that marks the object (the engine guards its writes; settings hide it from Objects). */
export const MEMBER_DIRECTORY_TRAIT = 'member_directory';

/** Stable key prefix of the built-in fields the system keeps in sync with the members table. */
export const MEMBER_CORE_STABLE_PREFIX = 'members:member.';

/**
 * Built-in fields mirrored from the members table (read only). Their keys are reserved: custom
 * member fields cannot use them, so `viewer.<key>` and `current_member.field.<key>` stay unambiguous.
 */
export const MEMBER_CORE_FIELDS = [
  { key: 'email', type: 'email', labelEn: 'Email', labelDe: 'E-Mail' },
  { key: 'job_title', type: 'text', labelEn: 'Job title', labelDe: 'Position' },
  { key: 'role', type: 'text', labelEn: 'Role', labelDe: 'Rolle' },
  { key: 'status', type: 'select', labelEn: 'Status', labelDe: 'Status' },
] as const;

/** Keys a custom member field may not use (built-ins and the names of the `viewer` root). */
export const MEMBER_RESERVED_KEYS: readonly string[] = [
  'id',
  'name',
  'initials',
  'title',
  'team',
  'teams',
  'role_key',
  'avatar',
  'avatar_color',
  'last_active',
  'created_at',
  'updated_at',
  ...MEMBER_CORE_FIELDS.map((f) => f.key),
];

/** Most custom member fields one workspace may define (shown as columns; keeps the list usable). */
export const MEMBER_FIELD_LIMIT = 60;

/**
 * Which member fields appear where. `list`: extra columns of Settings › Members; `card`: the
 * person card shown when a member is mentioned or picked. Keys are field api keys; absent = the
 * defaults (the first fields in field order).
 */
export const MemberFieldDisplay = z.object({
  list: z.array(z.string().max(63)).max(MEMBER_FIELD_LIMIT).nullable().default(null),
  card: z.array(z.string().max(63)).max(MEMBER_FIELD_LIMIT).nullable().default(null),
});
export type MemberFieldDisplay = z.infer<typeof MemberFieldDisplay>;

export const MemberFieldDisplayInput = MemberFieldDisplay.partial();
export type MemberFieldDisplayInput = z.input<typeof MemberFieldDisplayInput>;

/** Defaults when nothing is configured: up to this many custom fields show in the list / on the card. */
export const MEMBER_LIST_DEFAULT_COLUMNS = 3;
export const MEMBER_CARD_DEFAULT_FIELDS = 4;

/** Name of the settings row (per workspace) that stores the display configuration. */
export const MEMBER_FIELD_DISPLAY_SETTING = 'members.field_display';

/**
 * Variable of record policies and filters that reads a custom field of the signed-in member:
 * `{"$var": "current_member.field.<key>"}`.
 */
export const MEMBER_FIELD_VAR_PREFIX = 'current_member.field.';

export function memberFieldVar(key: string): string {
  return `${MEMBER_FIELD_VAR_PREFIX}${key}`;
}

/** Does the object carry the member directory trait? */
export function isMemberDirectory(object: { traits: readonly string[] }): boolean {
  return object.traits.includes(MEMBER_DIRECTORY_TRAIT);
}

/**
 * Which field types suit a people directory (what "Add field" offers on Members): pipeline stages and
 * autonumbers describe deals and tickets, not staff details, so they are not offered; the types admins
 * reach for first (a department, a location, a start date, a manager, skills) lead "Most used".
 * Types contributed by an extension are offered only when they declare they apply to the member
 * directory (`applies_to.traits`).
 */
export const MEMBER_FIELD_TYPE_PROFILE = {
  hidden: ['status', 'autonumber'] as readonly string[],
  popular: ['select', 'text', 'date', 'user', 'multi_select', 'number'] as readonly string[],
} as const;

/**
 * Does "Add field" offer a type on this object? Everywhere but the member directory every type is
 * offered; there, `MEMBER_FIELD_TYPE_PROFILE` applies and extension types must name the directory.
 */
export function fieldTypeFitsObject(
  type: {
    key: string;
    origin: 'builtin' | 'plugin';
    extension?: { appliesTo?: { objects?: string[]; traits?: string[] } | undefined } | undefined;
  },
  object: { key: string; traits: readonly string[] },
): boolean {
  const applies = type.extension?.appliesTo;
  if (applies && (applies.objects?.length || applies.traits?.length))
    return (
      Boolean(applies.objects?.includes(object.key)) ||
      Boolean(applies.traits?.some((tr) => object.traits.includes(tr)))
    );
  if (!isMemberDirectory(object)) return true;
  return type.origin === 'builtin' && !MEMBER_FIELD_TYPE_PROFILE.hidden.includes(type.key);
}

/** Defaults applied to the display configuration given the custom field keys in field order. */
export function resolveMemberFieldDisplay(
  config: MemberFieldDisplay | null | undefined,
  customKeys: readonly string[],
): { list: string[]; card: string[] } {
  const known = new Set(customKeys);
  const pick = (chosen: readonly string[] | null | undefined, fallback: number) =>
    (chosen ?? customKeys.slice(0, fallback)).filter((k) => known.has(k));
  return {
    list: pick(config?.list, MEMBER_LIST_DEFAULT_COLUMNS),
    card: pick(config?.card, MEMBER_CARD_DEFAULT_FIELDS),
  };
}

/** State of the member fields for the settings screen and the member list. */
export const MemberFieldsStateDTO = z.object({
  /** The `member` object exists (the first member field was added). */
  ready: z.boolean(),
  /** Custom (admin defined) field keys in field order. */
  customKeys: z.array(z.string()),
  /** Resolved display: list columns and person card fields (defaults applied). */
  display: z.object({ list: z.array(z.string()), card: z.array(z.string()) }),
  /** Stored configuration; null entries use the defaults. */
  stored: MemberFieldDisplay,
});
export type MemberFieldsStateDTO = z.infer<typeof MemberFieldsStateDTO>;
